import { AuthSession } from '../domain/entities/AuthSession';
import {
    AuthMode,
    AUTHORIZATION_HEADER_NAME,
    CARE_GUEST_SIGNIN_STATE_NAME,
    CARE_SIGNIN_DATA_NAME,
    CARE_SIGNIN_STATE_NAME,
    COOKIES_NAMES,
    TOKEN_TYPE
} from '../constants/constants';
import { BrowserStorage, IHttpTransport, IRequestTransformer } from '@estee/elc-universal-utils';
import { ISessionData, SignInApiSdk } from '../../api/SignInApiSdk';
import { ConfigStore } from '../../service-setup/ConfigStore';
import { IAccountConfig } from '../../service-setup/IAccountConfig';
import { IAuthRequestStateData } from '../domain/entities/IAuthRequestStateData';
import { encode } from 'base-64';

export interface IAuthSessionRepositoryConfig {
    authSession: AuthSession;
    careAuthSession: AuthSession;
    signInApiSdk: SignInApiSdk;
    httpTransport: IHttpTransport;
    configStore: ConfigStore;
}

const DEFAULT_EXPIRATION_TIME_MS = 1 * 60 * 60 * 1000;

export class AuthSessionRepository {
    private readonly authSession: AuthSession;
    private readonly careAuthSession: AuthSession;
    private readonly signInApiSdk: SignInApiSdk;
    private readonly httpTransport: IHttpTransport;
    private readonly businessUnitId: string;

    private requestTransformer: IRequestTransformer;

    public readonly enableCareConfig: boolean;

    private ensureAuthSessionPromise: Promise<void> | null = null;

    private ensureCareAuthSessionPromise: Promise<void> | null = null;
    private accessTokenExpirationDate: Date | null = null;

    constructor(config: IAuthSessionRepositoryConfig) {
        this.authSession = config.authSession;
        this.careAuthSession = config.careAuthSession;
        this.businessUnitId = config.configStore.config.businessUnitId;

        this.signInApiSdk = config.signInApiSdk;
        this.httpTransport = config.httpTransport;

        const accountConfig: IAccountConfig = config.configStore.config.accountConfig || {};
        this.enableCareConfig = accountConfig.authMode === AuthMode.CARE;
    }

    public get authRequestState() {
        return BrowserStorage.getItem(CARE_SIGNIN_STATE_NAME, 'localStorage');
    }

    public get guestAuthRequestState() {
        return BrowserStorage.getItem(CARE_GUEST_SIGNIN_STATE_NAME, 'localStorage');
    }

    public setAuthRequestState(state: string) {
        BrowserStorage.setItem(CARE_SIGNIN_STATE_NAME, state, 'localStorage');
    }

    public setGuestAuthRequestState(state: string) {
        BrowserStorage.setItem(CARE_GUEST_SIGNIN_STATE_NAME, state, 'localStorage');
    }

    public setAuthRequestStateData(data: IAuthRequestStateData | null) {
        if (!data) {
            BrowserStorage.removeItem(CARE_SIGNIN_DATA_NAME);
        } else {
            BrowserStorage.setItem(CARE_SIGNIN_DATA_NAME, JSON.stringify(data), 'localStorage');
        }
    }

    public getAuthRequestStateData(): IAuthRequestStateData | null {
        const serialized = BrowserStorage.getItem(CARE_SIGNIN_DATA_NAME, 'localStorage');

        return serialized ? (JSON.parse(serialized) as IAuthRequestStateData) : null;
    }

    public storeInCookie = () => {
        // handle auth token cookie storage
        this.storeAuthSession(this.authSession, COOKIES_NAMES.AUTH_TOKEN, COOKIES_NAMES.AUTH_TYPE);
    };

    public storeCareToken = () => {
        this.storeAuthSession(
            this.careAuthSession,
            COOKIES_NAMES.CARE_AUTH_TOKEN,
            COOKIES_NAMES.CARE_AUTH_TYPE
        );
    };

    private get storedAuthSession() {
        const tokenCookie = BrowserStorage.getCookie(COOKIES_NAMES.AUTH_TOKEN);
        const tokenTypeCookie = BrowserStorage.getCookie(COOKIES_NAMES.AUTH_TYPE);

        return new AuthSession(tokenCookie, tokenTypeCookie, null, null);
    }

    private storeAuthSession(
        authSession: AuthSession,
        cookieTokenName: string,
        cookieTypeName: string
    ) {
        const { token: authToken, tokenType, expiration } = authSession;

        if (!authToken) {
            BrowserStorage.eraseCookie(cookieTokenName);
            BrowserStorage.eraseCookie(cookieTypeName);
        } else {
            const storedToken = BrowserStorage.getCookie(cookieTokenName);
            const storedTokenType = BrowserStorage.getCookie(cookieTypeName);
            if (!storedToken || storedToken !== authToken || storedTokenType !== tokenType) {
                // update cookie if a new token is available
                BrowserStorage.setCookie(
                    cookieTokenName,
                    authToken,
                    new Date(Date.now() + (expiration || 0) * 1000)
                );
                BrowserStorage.setCookie(
                    cookieTypeName,
                    <string>tokenType,
                    new Date(Date.now() + (expiration || 0) * 1000)
                );
            }
        }
    }

    public getGuestAuthToken = () => {
        return {
            access_token: encode(`${this.businessUnitId}:${window.location.origin}`),
            expires_in: 86400,
            user_id: null
        };
    };

    public signOut = async () => {
        try {
            await this.signInApiSdk.signOut({ token: this.authSession.token });
        } catch (error) {
            throw error;
        }
    };

    public ensureAuthSession: () => Promise<void> = () => {
        let result: Promise<void> | null = this.ensureAuthSessionPromise;
        if (!result) {
            result = this.ensureAuthSessionPromise = this.doEnsureAuthSession();
        }

        return result as Promise<void>;
    };

    private doEnsureAuthSession = async () => {
        let guestToken;
        try {
            guestToken = await this.signInApiSdk.getLegacyGuestTokenIndependentFromApi();
            this.setGuestAuthToken(guestToken);
            this.storeInCookie();
        } catch (err) {
            console.error(err);
        }

        this.ensureAuthSessionPromise = null;
    };

    private createRequestTransformer = (): IRequestTransformer => {
        return async (config) => {
            const newHeaders = new Headers(config.headers);
            const token = this.getGuestAuthToken().access_token;

            if (!newHeaders.has(AUTHORIZATION_HEADER_NAME)) {
                newHeaders.set(AUTHORIZATION_HEADER_NAME, token as string);
            }

            config.headers = newHeaders;
        };
    };

    private setGuestAuthToken = (response: { access_token: string; expires_in: number | null }) => {
        const newSession = new AuthSession(
            response.access_token,
            TOKEN_TYPE.GUEST,
            response.expires_in,
            null
        );
        this.authSession.updateAuthSession(newSession);
    };

    public getLegacyAuthToken = async () => {
        const storedAuthToken = this.authSession.token || this.storedAuthSession;
        if (!this.authSession.token) {
            await this.ensureAuthSession();
        }

        return storedAuthToken;
    };

    public getAccessToken = async (): Promise<string> => {
        await this.ensureCareAuthSession();

        return this.careAuthSession.token ?? this.getGuestAuthToken().access_token;
    };

    public getUserId = async (): Promise<string> => {
        await this.ensureCareAuthSession();

        return this.careAuthSession.userId ?? '';
    };

    public isAuthenticated = async (): Promise<boolean> => {
        if (this.enableCareConfig) {
            await this.ensureCareAuthSession();

            return this.careAuthSession.isAuthenticated;
        }

        return this.authSession.isAuthenticated;
    };

    public clearSession() {
        this.authSession.clearToken();
        this.ensureAuthSessionPromise = null;
    }

    public clearCareSession() {
        this.careAuthSession.clearToken();

        this.accessTokenExpirationDate = null;
        this.ensureCareAuthSessionPromise = null;
    }

    public ensureCareAuthSession = async (): Promise<void> => {
        // ensure token is not stale
        if (
            this.ensureCareAuthSessionPromise &&
            this.accessTokenExpirationDate &&
            this.accessTokenExpirationDate < new Date()
        ) {
            this.accessTokenExpirationDate = new Date(
                new Date().getTime() + DEFAULT_EXPIRATION_TIME_MS
            );
            this.ensureCareAuthSessionPromise = null;
        }

        if (!this.ensureCareAuthSessionPromise) {
            this.ensureCareAuthSessionPromise = this.doEnsureCareAuthSession();
        }

        return this.ensureCareAuthSessionPromise;
    };

    private doEnsureCareAuthSession = async (): Promise<void> => {
        let session: ISessionData | null = null;
        try {
            const { data } = await this.signInApiSdk.getSession();
            if (data.access_token) {
                session = data;
            }
        } catch (err) {
            //
        }

        const accessToken: {
            access_token: string;
            expires_in: number | null;
            user_id: string | null;
        } =
            session && session.access_token
                ? {
                      access_token: session.access_token,
                      expires_in: session.expires,
                      user_id: session.user_id
                  }
                : this.getGuestAuthToken();

        this.accessTokenExpirationDate = new Date(
            new Date().getTime() + (accessToken.expires_in || DEFAULT_EXPIRATION_TIME_MS)
        );

        const newSession = new AuthSession(
            accessToken.access_token,
            accessToken.user_id ? TOKEN_TYPE.USER : TOKEN_TYPE.GUEST,
            accessToken.expires_in,
            accessToken.user_id
        );

        this.careAuthSession.updateAuthSession(newSession);
    };

    public ensureRequestTransformer = () => {
        if (!this.requestTransformer) {
            this.requestTransformer = this.createRequestTransformer();
            this.httpTransport.addRequestTransformer(this.requestTransformer);
        }
    };
}
